import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { AiOutlineEdit } from 'react-icons/ai';
import { RiDeleteBinLine } from 'react-icons/ri';

import IntegrationModal from '@components/modals/IntegrationsModal/IntegrationsModal';
import { CancelButton, SaveButton } from '@lib/core/src/components/Buttons';
import palette from '@lib/core/src/theme/palette';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { EVENTS } from '@/analytics/consts';
import { useAnalytics } from '@/analytics/useAnalytics';
import {
  MeetingTemplateDto,
  MeetingTemplateDtoProvider,
  MeetingTemplateResponse,
  UnifiedConnectionDtoProvider,
  useCreateMeetingTemplate,
  useDeleteMeetingTemplate,
  usePatchMeetingTemplate
} from '@/api/generated';
import { ChecklistItemDtoStatus } from '@/api/generated';
import { BlockModal } from '@/components/modals/BlockModal/BlockModal';
import useConnections from '@/hooks/Integrations/useConnections';
import { useCheckList } from '@/hooks/useCheckList';
import { CheckListEnumTitles } from '@/pages/HomePage/components/checklist/CheckListConfig';
import { useNativeAgents } from '@/pages/JamiesCapabilities/components/hooks/useNativeAgents';

import { AgentTemplateIntegrationSection } from './AgentTemplateIntegrationSection';
import {
  AddBlockButton,
  DescrepctionTitle,
  SectionCard,
  SectionCardContainer,
  TemplateConfigItemsTitle
} from './AgentTemplateStyles';

const buttonsStyling = {
  width: 'auto',
  minWidth: '60px',
  height: 'auto',
  padding: '8px 15px'
};

export const AgentTemplateConfig = ({
  template,
  setSelectTemplate,
  isPredefinedTemplateRef
}: {
  template: MeetingTemplateDto;
  isPredefinedTemplateRef: React.MutableRefObject<boolean>;
  setSelectTemplate: React.Dispatch<React.SetStateAction<MeetingTemplateDto | null>>;
}) => {
  const { toggleUserAgentsConfig } = useNativeAgents();
  const { isItemCompleted, updateCheckList } = useCheckList();
  const isTemplateActivated = isItemCompleted(CheckListEnumTitles.ActivateOneTemplate);
  const queryClient = useQueryClient();
  const [tags, setTags] = useState<string[]>(template.tags || []);
  const [open, setOpen] = useState<boolean>(false);
  const [openBlockModal, setOpenBlockModal] = useState<boolean>(false);
  const [name, setName] = useState<string>(template.name || '');
  const [provider, setProvider] = useState<MeetingTemplateDtoProvider | undefined>(
    template.provider
  );
  const [context, setContext] = useState<string>(template.context || '');
  const selectedBlockDetails = useRef<{
    title?: string;
    prompt?: string;
    isEdit: boolean;
    blockIndex?: number;
  }>();
  const { conntectionList } = useConnections();
  const { captureEvent } = useAnalytics();
  const { mutate: deleteTemplate, isPending: isPendingDelete } = useDeleteMeetingTemplate({
    mutation: { onSuccess: async () => await onActionEnd() }
  });
  const { mutate: updateTemplate, isPending: isPendingUpdate } = usePatchMeetingTemplate({
    mutation: {
      onSuccess: async () => {
        captureEvent(EVENTS.AGENT_TEMPLATE_EDITED, {
          templateId: template.id,
          templateName: template.name
        });
        await onActionEnd();
      }
    }
  });
  const { mutate: createTemplate, isPending: isPendingCreate } = useCreateMeetingTemplate({
    mutation: {
      onSuccess: async (template: MeetingTemplateResponse) => {
        captureEvent(EVENTS.AGENT_TEMPLATE_CREATED, {
          templateId: template.items?.[0]?.id,
          templateName: template.items?.[0]?.name
        });
        await onActionEnd();
      }
    }
  });
  const [localSections, setLocalSections] = useState(template.sections || []);
  const [inputKeyword, setInputKeyword] = useState('');

  useEffect(() => {
    setLocalSections(template.sections || []);
  }, [template.sections]);

  const onDelete = () => {
    if (!template.id) return;
    deleteTemplate({ id: template.id });
  };

  const onSave = () => {
    const data = { tags, context, name, provider, sections: localSections };
    if (isPredefinedTemplateRef.current) {
      createTemplate(
        { data },
        {
          onSuccess: () => {
            toggleUserAgentsConfig('meetingSummary', true);
            if (isTemplateActivated) return;
            updateCheckList({
              title: CheckListEnumTitles.ActivateOneTemplate,
              status: ChecklistItemDtoStatus.completed
            });
          }
        }
      );
      return;
    }
    if (template.id) updateTemplate({ id: template.id, data });
  };

  const onActionEnd = async () => {
    await queryClient.invalidateQueries({ queryKey: ['listMeetingsTemplates'] });
    // eslint-disable-next-line no-param-reassign
    isPredefinedTemplateRef.current = false;
    setSelectTemplate(null);
  };

  const isLoadingSave = isPendingUpdate || isPendingCreate;

  const isIntegratedWithHubSpot = useMemo(
    () =>
      conntectionList?.find(conn => conn.provider === UnifiedConnectionDtoProvider.hubspot) !==
      undefined,
    [conntectionList]
  );
  const openEditCreateBlock = useCallback(
    ({
      title,
      prompt,
      blockIndex,
      isEdit
    }: {
      title?: string;
      prompt?: string;
      blockIndex?: number;
      isEdit: boolean;
    }) => {
      selectedBlockDetails.current = { title, prompt, isEdit, blockIndex };
      setOpenBlockModal(true);
    },
    []
  );

  const onTemplateBlockUpdate = useCallback((title: string, prompt: string) => {
    if (!selectedBlockDetails.current?.isEdit) {
      setLocalSections(prev => [
        ...prev,
        {
          title,
          prompt,
          index: prev.length > 0 ? Math.max(...prev.map(s => s.index ?? 0)) + 1 : 1
        }
      ]);
    } else {
      setLocalSections(prev =>
        prev.map(section =>
          section.index === selectedBlockDetails.current?.blockIndex
            ? { ...section, title, prompt }
            : section
        )
      );
    }
  }, []);

  const onDeleteBlock = useCallback((blockIndex?: number) => {
    if (!blockIndex) return;
    setLocalSections(prev => prev.filter(section => section.index !== blockIndex));
  }, []);

  useEffect(() => {
    setName(template.id === 'custom' ? '' : template.name || '');
  }, [template.id, template.name]);

  return (
    <Box
      sx={{
        paddingRight: {
          md: 2,
          lg: 8
        },
        flex: 1,
        height: '100%'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: { md: 1, lg: 5 } }}>
        <IconButton
          sx={{
            padding: '8px',
            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
          }}
          onClick={() => setSelectTemplate(null)}
        >
          <ArrowBackIosIcon fontSize="small" />
          <Typography sx={{ color: palette.grey[500] }}>Back</Typography>
        </IconButton>
      </Box>

      <Stack
        gap={{ md: 2, lg: 4 }}
        paddingRight={'10px'}
        overflow={'auto'}
        height={{ md: 'calc(100% - 93px)', lg: 'calc(100% - 133px)' }}
      >
        <Box>
          <TemplateConfigItemsTitle>Write a title for your template</TemplateConfigItemsTitle>
          <TextField
            fullWidth
            size="small"
            value={name}
            onChange={e => setName(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '7px',
                background: palette.white
              }
            }}
          />
        </Box>
        <Box>
          <TemplateConfigItemsTitle>
            Automatically apply template if the meeting title contains (required)
          </TemplateConfigItemsTitle>

          <Autocomplete
            clearIcon={false}
            options={[]}
            freeSolo
            multiple
            limitTags={6}
            value={tags}
            inputValue={inputKeyword}
            onInputChange={(_, value) => setInputKeyword(value)}
            onChange={(e, value) => setTags(value)}
            renderTags={(value, props) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...props({ index })}
                  key={`${value}-${index}`}
                  deleteIcon={
                    <IconButton
                      sx={{
                        padding: '2px',
                        '&:hover': {
                          background: 'transparent'
                        }
                      }}
                    >
                      <CloseIcon fontSize="small" sx={{ color: palette.black }} />
                    </IconButton>
                  }
                  sx={{ background: palette.grey[800], color: palette.black }}
                />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '7px',
                    background: palette.white
                  }
                }}
                onBlur={e => {
                  const trimmed = inputKeyword.trim();
                  if (trimmed && !tags.includes(trimmed)) {
                    setTags([...tags, trimmed]);
                  }
                  setInputKeyword('');
                }}
              />
            )}
          />
          <DescrepctionTitle>Type a keyword, then hit Enter</DescrepctionTitle>
        </Box>

        <Box>
          <TemplateConfigItemsTitle>Context prompt</TemplateConfigItemsTitle>
          <TextField
            multiline
            fullWidth
            rows={3}
            onChange={e => setContext(e.target.value)}
            slotProps={{
              input: {
                sx: {
                  fontSize: 12
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '7px',
                padding: '8.5px 14px',
                background: palette.white
              }
            }}
            value={context}
            variant="outlined"
          />
        </Box>
        <AgentTemplateIntegrationSection
          isIntegratedWithHubSpot={isIntegratedWithHubSpot}
          provider={provider}
          setProvider={setProvider}
          setOpen={setOpen}
        />
        <Box>
          <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <TemplateConfigItemsTitle>Summary Structure</TemplateConfigItemsTitle>
            <AddBlockButton
              onClick={() =>
                openEditCreateBlock({
                  isEdit: false
                })
              }
            >
              + Add block
            </AddBlockButton>
          </Stack>
          <SectionCardContainer>
            {localSections.map(section => (
              <SectionCard key={section.index}>
                <Typography fontSize={13}>{section.title}</Typography>
                <Stack alignItems={'center'} flexDirection={'row'}>
                  <IconButton
                    onClick={() =>
                      openEditCreateBlock({
                        blockIndex: section.index,
                        title: section.title,
                        prompt: section.prompt,
                        isEdit: true
                      })
                    }
                  >
                    <AiOutlineEdit size={14} color={palette.grey[500]} />
                  </IconButton>
                  <IconButton onClick={() => onDeleteBlock(section.index)}>
                    <RiDeleteBinLine size={14} color={palette.grey[500]} />
                  </IconButton>
                </Stack>
              </SectionCard>
            ))}
          </SectionCardContainer>
        </Box>
      </Stack>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          paddingTop: 1
        }}
      >
        {!isPredefinedTemplateRef.current && (
          <CancelButton
            sx={{ ...buttonsStyling }}
            onClick={onDelete}
            disabled={isPendingDelete || isPredefinedTemplateRef.current}
          >
            {isPendingDelete ? <CircularProgress size={10} color={'inherit'} /> : 'Delete'}
          </CancelButton>
        )}
        <SaveButton
          sx={{ ...buttonsStyling }}
          disabled={
            isLoadingSave ||
            !tags.length ||
            (template.id === 'custom' && (!localSections || localSections.length === 0))
          }
          onClick={onSave}
        >
          {isLoadingSave ? <CircularProgress size={10} color={'inherit'} /> : 'Save'}
        </SaveButton>
      </Box>
      <IntegrationModal open={open} onClose={() => setOpen(false)} />
      <BlockModal
        isEdit={!!selectedBlockDetails.current?.isEdit}
        title={selectedBlockDetails.current?.title}
        prompt={selectedBlockDetails.current?.prompt}
        open={openBlockModal}
        onConfirm={onTemplateBlockUpdate}
        onClose={() => {
          selectedBlockDetails.current = undefined;
          setOpenBlockModal(false);
        }}
      />
    </Box>
  );
};
